import styles from './TimePicker.module.scss';

import React, { useEffect, useState } from 'react';
import moment, { Moment } from 'moment';

import { Callout, DirectionalHint, Link, Stack, TextField } from '@fluentui/react';
import { css } from '../../util/Css';

const TIME_FORMAT = 'h:mm a';
const hourOptions = Array.from({ length: 12 }, (_, i) => ({ text: '' + (i + 1), key: '' + (i + 1) }));
const minuteOptions = Array.from({ length: 60 }, (_, i) => ({ text: '' + i, key: '' + i }));
const meridiemOptions = [
  { text: 'am', key: 'am' },
  { text: 'pm', key: 'pm' },
];

interface Props {
  value?: Moment;
  onChange?: (value: Moment) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
}

export const TimePicker = ({ value, onChange, label, required, disabled }: Props) => {
  const [datetime] = useState(value ? moment(value) : moment());
  const [parts, setParts] = useState({ hour: datetime.format('h'), minute: datetime.format('m'), meridiem: datetime.format('a') });

  // display text for the input control
  const [text, setText] = useState(datetime.format(TIME_FORMAT));

  const [openCallout, setOpenCallout] = useState(false);

  const wrapperDiv = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const current = moment(`${datetime.format(moment.HTML5_FMT.DATE)}T${text}`, `${moment.HTML5_FMT.DATE}T${TIME_FORMAT}`);
    if (current.isValid()) {
      if (!openCallout) {
        setParts({
          hour: current.format('h'),
          minute: current.format('m'),
          meridiem: current.format('a'),
        });
      }
      if (onChange) {
        onChange(current);
      }
    }
    // Intentional to not have onChange, openCallout in dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, datetime]);

  useEffect(() => {
    if (openCallout) {
      setText(moment(`${parts.hour}:${parts.minute} ${parts.meridiem}`, TIME_FORMAT).format(TIME_FORMAT));
    }
    // Intentional to not have openCallout in dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parts]);

  const resetToNow = () => {
    const m = moment();
    setParts({
      hour: m.format('h'),
      minute: m.format('m'),
      meridiem: m.format('a'),
    });
    setText(m.format(TIME_FORMAT));
    setOpenCallout(false);
  };

  const onTextChange = (ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, value?: string) => {
    setOpenCallout(false);
    setText(value || '');
  };

  return (
    <>
      <div>
        <div ref={wrapperDiv} data-tid="ctn_timeSelector">
          <TextField
            label={label || 'Time'}
            required={required}
            disabled={disabled}
            value={text}
            iconProps={{
              iconName: 'Clock',
              style: { pointerEvents: 'auto', cursor: 'pointer' },
              onClick: () => {
                if (disabled) {
                  return;
                }
                const updated = moment(text, TIME_FORMAT);
                if (updated.isValid()) {
                  setParts({
                    hour: updated.format('h'),
                    minute: updated.format('m'),
                    meridiem: updated.format('a'),
                  });
                }
                setOpenCallout(true);
              },
            }}
            onChange={onTextChange}
          />
        </div>
      </div>

      {openCallout && (
        <Callout target={wrapperDiv.current} directionalHint={DirectionalHint.bottomLeftEdge} beakWidth={0} onDismiss={() => setOpenCallout(!openCallout)} setInitialFocus>
          <Stack>
            <Stack horizontal>
              <TimePart options={hourOptions} onSelect={(k) => setParts({ ...parts, hour: k })} value={parts.hour} tid="dd_hour" />
              <TimePart options={minuteOptions} onSelect={(k) => setParts({ ...parts, minute: k })} value={parts.minute} tid="dd_minute" />
              <TimePart options={meridiemOptions} onSelect={(k) => setParts({ ...parts, meridiem: k })} value={parts.meridiem} tid="dd_meridiem" />
            </Stack>
            <Link className={styles.nowLink} onClick={resetToNow}>
              Now
            </Link>
          </Stack>
        </Callout>
      )}
    </>
  );
};

interface TimePartProps {
  options: { text: string; key: string }[];
  onSelect: (value: string) => void;
  value: string;
  tid: string;
}

const TimePart = ({ options, onSelect, value, tid }: TimePartProps) => {
  const refs: { [key: string]: any } = options.reduce((acc: { [key: string]: React.RefObject<unknown> }, value, tid) => {
    acc[value.key] = React.createRef();
    return acc;
  }, {});

  useEffect(() => {
    refs[value].current.scrollIntoView();
    // Intentional to not have refs, value in dependencies (this should happen once)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.timePartContainer} data-tid={tid}>
      {options.map((o) => (
        <div ref={refs[o.key]} className={css(styles.item, { [styles.selected]: o.key === value })} key={o.key} onClick={() => onSelect(o.key)}>
          {o.key}
        </div>
      ))}
    </div>
  );
};
